import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useReportContext } from 'lib/context/ReportContext/ReportContext';
import useLoading from 'lib/hooks/useLoading';

import Button from 'components/shared/Buttons/Button/Button';
import Export from 'components/shared/Export/Export';
import Filters from 'components/shared/Filters/Filters';

import SaveCustomReportModal from './components/SaveCustomReportModal';
import { expandCollapseNodes } from './lib/helpers';

const BackButton = () => {
  const navigate = useNavigate();

  return (
    <Button
      text="Go back"
      variant="link"
      size="xs"
      onClick={() => navigate(-1)}
      transparent
      icon="arrow-left-linear"
    />
  );
};

const ReportHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [allExpanded, setAllExpanded] = useState(false);

  const { loading: isExpanding, startLoading, stopLoading } = useLoading();
  const {
    gridRef,
    report,
    isLoading,
    filters,
    setFilters,
    reportId,
    customReport,
    columnDefs: colDefs,
    defaultFilters
  } = useReportContext();

  const { name, result, statusPanelData } = report || {};

  const toggleExpandCollapse = async () => {
    if (!gridRef?.current?.api) return;

    startLoading();

    await expandCollapseNodes(gridRef, allExpanded);
    setAllExpanded((prev) => !prev);

    stopLoading();
  };

  const hasChildren = !!result?.some((record) => record?.children?.length);
  return (
    <div className="relative flex flex-col gap-3 border-0 border-b border-solid border-neutral-100 bg-white p-2">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-1">
          <BackButton />
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <Export
            showLabel
            useData={() => ({
              title: name,
              gridApi: gridRef?.current?.api,
              colDefs,
              statusPanelData,
              printExpandedRows: allExpanded
            })}
          />

          <Button
            size="xs"
            variant="tertiary"
            text="Customize"
            icon="sliders-horizontal-linear"
            onClick={() =>
              navigate(location.pathname + location.search, {
                replace: true,
                state: { ...location.state, customize: true }
              })
            }
          />

          {!isLoading && reportId && (
            <SaveCustomReportModal
              reportId={reportId}
              name={name}
              filters={filters}
              customReport={customReport}
            />
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-3">
          <Filters filters={filters} setFilters={setFilters} defaultFilters={defaultFilters} />
        </div>

        {hasChildren && (
          <Button
            size="xs"
            variant="link"
            color="neutral"
            className="whitespace-nowrap"
            text={`${allExpanded ? 'Collapse' : 'Expand'} All`}
            onClick={toggleExpandCollapse}
            disabled={isExpanding}
            loadingIcon={isExpanding}
          />
        )}
      </div>
    </div>
  );
};

export default ReportHeader;
